import React from 'react';
import {
  IModalDownStreamsConfigurations,
  MESSAGES_TYPES,
  getDownStreamStatusConfigurations
} from 'utils/downstreams/DownStreamModalConfigurations';
import { StatusViewWrapper } from './style';
import { Grid } from 'semantic-ui-react';
import StatusAlert from './StatusAlert';
import { FEATURES, getFeatureAvailability } from 'utils/featureToggle';
import {
  DOWNSTREAM_SYNC_STATUS,
  normalizeAthenaStatus
} from 'utils/constants/planner';
import { useMutation } from 'react-apollo';
import {
  RETRY_LAST_SYNC_ACTION,
  FORCE_FAIL_ACTION
} from 'api/graphql/v2/mutations/Event';
import { IEvent } from 'model/v2';

interface Props {
  configurations?: IModalDownStreamsConfigurations;
  event: IEvent;
  reloadEvent: () => void;
  isLoading?: boolean;
}
const StatusView: React.FC<Props> = ({ event, reloadEvent }: Props) => {
  const [syncTrials, setSyncTrials] = React.useState(
    event?.syncAction?.retryCount || 0
  );
  const [retrySync, { loading: retryIsLoading }] = useMutation(
    RETRY_LAST_SYNC_ACTION,
    {
      onCompleted: data => {
        setSyncTrials(data.retryLastSyncAction.retryCount);
        reloadEvent();
      }
    }
  );
  const [forceFail, { loading: forceFailIsLoading }] = useMutation(
    FORCE_FAIL_ACTION,
    {
      onCompleted: () => {
        reloadEvent();
      }
    }
  );

  const eventConfigurations = getDownStreamStatusConfigurations(
    event?.crStatus,
    event?.athenaStatus,
    false,
    event?.appointmentType?.eventType?.name
  );
  const [onRetryLoading, setOnRetryLoading] = React.useState(false);
  const maxResyncTrials = 3;

  React.useEffect(() => {
    setOnRetryLoading(false);
  }, [eventConfigurations]);

  const resync = () => {
    if (!event || !event.id) {
      return;
    }
    if (!retryIsLoading) {
      setOnRetryLoading(true);
      retrySync({ variables: { eventId: event.id } });
    }
  };

  const handleForceFail = () => {
    if (!event || !event.id) {
      return;
    }
    if (!forceFailIsLoading) {
      forceFail({ variables: { eventId: event.id } });
    }
  };

  // CR status surfaces are hidden behind the CR_SYNC_DISABLED feature flag.
  // Athena badge is hidden when NO_SYNC. If nothing is left to show, render
  // nothing so the wrapper's spacing doesn't leave an empty gap above details.
  const crStatusHidden = getFeatureAvailability(FEATURES.CR_SYNC_DISABLED);
  const athenaFailed =
    eventConfigurations.athenaStatusMessageType === MESSAGES_TYPES.fail;
  const crFailed =
    !crStatusHidden &&
    eventConfigurations.crStatusMessageType === MESSAGES_TYPES.fail;
  const showSyncFailure = athenaFailed || crFailed;
  const showCrStatus = !crStatusHidden;
  // Hide the Athena badge for DT when Athena display is disabled (normalized to
  // NO_SYNC), in addition to genuinely NO_SYNC events.
  const showAthenaStatus =
    normalizeAthenaStatus(
      event?.athenaStatus,
      event?.appointmentType?.eventType?.name
    ) !== DOWNSTREAM_SYNC_STATUS.NO_SYNC;
  if (!showAthenaStatus && !showCrStatus && !showSyncFailure) {
    return null;
  }

  return (
    <StatusViewWrapper>
      <Grid columns={2}>
        {getFeatureAvailability(FEATURES.RETRY_SYNC) &&
          showSyncFailure &&
          syncTrials < maxResyncTrials && (
            <Grid.Row>
              <p>**These appointments failed to sync. Please try again…</p>
            </Grid.Row>
          )}
        {showSyncFailure && syncTrials >= maxResyncTrials && (
          <Grid.Row>
            <p>
              This event has failed over 3 times. Try waiting a while before
              trying again.
            </p>
          </Grid.Row>
        )}
        <Grid.Row>
          <Grid.Column width={11}>
            {/* Athena: show every status except NO_SYNC (don't surface "Does not sync with Athena"). */}
            {showAthenaStatus && (
              <StatusAlert
                statusMessage={eventConfigurations.athenaStatusMessage}
                statusType={eventConfigurations.athenaStatusMessageType}
              />
            )}
            {/* CR badge shown only when CR status is not hidden by the feature flag. */}
            {showCrStatus && (
              <StatusAlert
                statusMessage={eventConfigurations.crStatusMessage}
                statusType={eventConfigurations.crStatusMessageType}
              />
            )}
          </Grid.Column>
          {showSyncFailure && (
            <Grid.Column floated="right" width={5}>
              {getFeatureAvailability(FEATURES.RETRY_SYNC) && (
                <button
                  className={
                    onRetryLoading ? 'resync-button onLoading' : 'resync-button'
                  }
                  onClick={resync}
                  disabled={onRetryLoading}
                >
                  Start Re-sync
                </button>
              )}
            </Grid.Column>
          )}
          {eventConfigurations.cancelDisabled && (
            <Grid.Column floated="right" width={5}>
              {getFeatureAvailability(FEATURES.FORCE_FAIL) && (
                <button
                  className={
                    forceFailIsLoading
                      ? 'force-fail-button onLoading'
                      : 'force-fail-button'
                  }
                  onClick={handleForceFail}
                  disabled={forceFailIsLoading}
                >
                  Force Fail
                </button>
              )}
            </Grid.Column>
          )}
        </Grid.Row>
      </Grid>
    </StatusViewWrapper>
  );
};
export default React.memo(StatusView);
